import type * as React from 'react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export function Section({
  title,
  children,
  testId,
}: {
  title: string;
  children: React.ReactNode;
  /**
   * A stable hook for the e2e suite. The dashboard spec used to locate tiles by
   * their Tailwind classes (`.text-3xl.tabular-nums`), which stopped matching
   * the moment `KpiCard` was restyled and matched nothing at all thereafter
   * (#532).
   */
  testId?: string;
}) {
  return (
    <section className="space-y-2" data-testid={testId}>
      <h3 className="text-sm font-semibold text-muted-foreground">{title}</h3>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{children}</div>
    </section>
  );
}

/** Compact KPI card (p-3, text-lg). Optional sign tone + vs-previous delta. */
export function KpiCard({
  icon,
  label,
  value,
  tone,
  delta,
}: {
  icon: React.ReactNode;
  /** A plain string is truncated; a node (e.g. a label carrying its own info
   * icon) renders as-is so the icon beside it is never clipped away. */
  label: React.ReactNode;
  value: string;
  tone?: 'positive' | 'negative';
  delta?: React.ReactNode;
}) {
  return (
    <Card className="p-3">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        {icon}
        {typeof label === 'string' ? <span className="truncate">{label}</span> : label}
      </div>
      <div
        data-testid="kpi-value"
        className={cn(
          'mt-1 text-lg font-semibold tabular-nums',
          tone === 'positive' && 'text-emerald-600 dark:text-emerald-400',
          tone === 'negative' && 'text-destructive',
        )}
      >
        {value}
      </div>
      {delta != null ? <div className="mt-0.5">{delta}</div> : null}
    </Card>
  );
}
